// A bucketed distribution over a SharedArrayBuffer (P2.10, extracted from P0.12).
//
// P0.12 wrote this arithmetic for frame times and it was right about its shape — "everything here
// is pure: it takes an Int32Array and returns numbers" — but it lived in /visual/core, behind a
// boundary the audio thread may not cross. `.dependency-cruiser.cjs`'s `worklet-isolation` rule
// whitelists /audio/dsp, /platform/types and /platform/sab and nothing else, which is not an
// inconvenience to route around: the worklet must not be able to reach a renderer by accident.
//
// So the arithmetic moves down to where both callers can legally stand, and gains one parameter —
// the spec — instead of reading /visual's frame constants. `frame-histogram.ts` keeps every
// signature it had and passes FRAME_HISTOGRAM; the latency probe passes LATENCY_HISTOGRAM.
//
// **Why a histogram at all, and not a list of samples.** Recording must be O(1), allocation-free
// and safe from two threads at once. `Atomics.add` into a fixed bucket is one instruction; a
// growable array of samples would allocate on a 2.7 ms audio deadline, and an allocation there is
// a future GC pause, which is the click NFR-P2 budgets. The cost is resolution — a percentile read
// from a histogram is accurate to one bucket, never better — and the honest way to report that is
// the bucket's upper edge, plus a `bucketMs` field so no reader infers precision that is not there.

/**
 * The shape of one histogram: how many buckets, how wide, and which one catches the tail.
 *
 * A parameter rather than a constant, because the two users are measuring different things and
 * have no reason to stay agreed. Both specs happen to match today; the type is what lets them stop
 * matching without a rewrite.
 */
export interface HistogramSpec {
  readonly BUCKETS: number;
  /** Bucket i covers [i·BUCKET_MICROS, (i+1)·BUCKET_MICROS). */
  readonly BUCKET_MICROS: number;
  /** The last bucket, unbounded: everything past the range lands there rather than being lost. */
  readonly OVERFLOW_BUCKET: number;
}

const MICROS_PER_MS = 1000;

/**
 * Which bucket a duration lands in.
 *
 * Clamped at both ends. Negative cannot happen from a monotonic clock, but a clamp is cheaper than
 * a proof and an out-of-range `Atomics.add` would corrupt whatever sits next to the histogram.
 */
export function bucketFor(spec: HistogramSpec, micros: number): number {
  if (micros < 0) {
    return 0;
  }
  const bucket = Math.floor(micros / spec.BUCKET_MICROS);
  return bucket >= spec.OVERFLOW_BUCKET ? spec.OVERFLOW_BUCKET : bucket;
}

/**
 * Record one sample. Called on a real-time budget, from whichever thread is producing.
 *
 * One `Atomics.add`. No allocation, no branch worth naming, no lock — which is the entire reason
 * the distribution is a histogram rather than a list of samples.
 */
export function recordSample(
  spec: HistogramSpec,
  histogram: Int32Array,
  baseI32: number,
  micros: number,
): void {
  Atomics.add(histogram, baseI32 + bucketFor(spec, micros), 1);
}

/** Total samples in a histogram. */
export function totalCount(
  spec: HistogramSpec,
  histogram: Int32Array,
  baseI32: number,
): number {
  let total = 0;
  for (let i = 0; i < spec.BUCKETS; i += 1) {
    total += Atomics.load(histogram, baseI32 + i);
  }
  return total;
}

/**
 * The q-th percentile, in microseconds, as a bucket's **upper** edge.
 *
 * Upper rather than lower, or the midpoint, and it is a deliberate bias: every number this feeds
 * is a budget check ("is p90 under 35 ms?"). Reporting the lower edge would let a sample that
 * genuinely missed the budget be reported as having met it, and a measurement that flatters the
 * code is worse than none. Round the wrong way on purpose, and say so.
 *
 * Returns null for an empty histogram rather than 0. Zero is a real, excellent reading and
 * "nothing was measured" is a broken harness; a function that returns the same value for both is
 * how `0 <= limit` passes vacuously — the failure this repo has now met at P0.1, P0.5, P0.8, P0.10
 * and P0.11, and again just before P2.10, where a deck born at rate 0 played silence past 484
 * green tests.
 */
export function percentileMicros(
  spec: HistogramSpec,
  histogram: Int32Array,
  baseI32: number,
  quantile: number,
): number | null {
  const total = totalCount(spec, histogram, baseI32);
  if (total === 0) {
    return null;
  }

  // `ceil`, so p100 is the last sample rather than one past it, and p50 of two samples is the
  // second. Standard nearest-rank.
  const rank = Math.max(1, Math.ceil(quantile * total));
  let seen = 0;

  for (let i = 0; i < spec.BUCKETS; i += 1) {
    seen += Atomics.load(histogram, baseI32 + i);
    if (seen >= rank) {
      return (i + 1) * spec.BUCKET_MICROS;
    }
  }

  // Unreachable: `seen` ends at `total` and `rank <= total`. Kept because the alternative is
  // TypeScript being told to trust arithmetic it cannot check.
  return spec.BUCKETS * spec.BUCKET_MICROS;
}

/**
 * Samples **strictly slower** than a threshold — "how many frames missed 16.7 ms" (NFR-P3).
 *
 * Counting starts one bucket *past* the threshold's own bucket, and that `+ 1` is the entire point
 * of this function. A histogram cannot answer "how many samples exceed X" exactly when X falls
 * inside a bucket: at 250 µs resolution, bucket 66 spans [16.50, 16.75) ms and holds both a
 * flawless 16.667 ms frame and a slightly-late 16.74 ms one. They are indistinguishable here.
 *
 * So the question is which way to be wrong, and it was answered by measurement rather than taste.
 * Counting from the threshold's own bucket — the obvious reading of "at or above" — reported
 * **900 of 900 perfect 60 Hz frames as over budget**, because 16.667 ms *is* the 60 Hz target and
 * the target sits in the same bucket as the frames that meet it. A metric that calls every good
 * frame a dropped one is not conservative, it is broken, and it would have been ignored inside a
 * week.
 *
 * The cost of the `+ 1` is that a sample up to one bucket late is not counted. The number this
 * returns is therefore a lower bound, and honest about it.
 */
export function countSlowerThan(
  spec: HistogramSpec,
  histogram: Int32Array,
  baseI32: number,
  micros: number,
): number {
  const from = bucketFor(spec, micros) + 1;
  let count = 0;
  for (let i = from; i < spec.BUCKETS; i += 1) {
    count += Atomics.load(histogram, baseI32 + i);
  }
  return count;
}

/** Samples in the overflow bucket — readings so slow the histogram stopped resolving them. */
export function overflowCount(
  spec: HistogramSpec,
  histogram: Int32Array,
  baseI32: number,
): number {
  return Atomics.load(histogram, baseI32 + spec.OVERFLOW_BUCKET);
}

export function clearHistogram(
  spec: HistogramSpec,
  histogram: Int32Array,
  baseI32: number,
): void {
  for (let i = 0; i < spec.BUCKETS; i += 1) {
    Atomics.store(histogram, baseI32 + i, 0);
  }
}

/** A distribution, in the units a human reads and a budget is written in. */
export interface DistributionMs {
  readonly count: number;
  readonly p50: number | null;
  readonly p90: number | null;
  readonly p95: number | null;
  readonly p99: number | null;
  readonly max: number | null;
  /** Samples past the last bucket. Non-zero means `max` is a floor, not a value. */
  readonly overflow: number;
  /** The resolution of every figure above. Reported so nobody reads more precision than exists. */
  readonly bucketMs: number;
}

/**
 * Read the whole distribution out, in milliseconds.
 *
 * `offsetMs` is added to every percentile, and it exists for exactly one caller: the latency probe
 * measures command→applied on the audio thread, and the speaker is another `outputLatency` beyond
 * that. Adding it afterwards keeps the audio thread's write to a single `Atomics.add` that knows
 * nothing about output devices. Zero for every other caller.
 *
 * ⚠️ **This used to say the gap is "a constant for the life of the context". It is not** (P4.14).
 * `AudioContext.outputLatency` tracks the real output path and moves — when the device changes,
 * when the buffer size changes, when playback stops. The premise mattered because it licensed
 * reading the value separately at each call site, and the 11 September bench did: its
 * `maxCommandLatencyMs` was offset by 40 ms and its histogram by 48, with a garbage-collection
 * pause between the two reads, producing a report whose stated maximum was 5 ms *below* its own
 * p50. It read as a counting bug for a day.
 *
 * Adding the offset afterwards is still right. What is not right is reading it more than once: a
 * caller building one report must take one reading and pass it everywhere.
 */
export function readDistribution(
  spec: HistogramSpec,
  histogram: Int32Array,
  baseI32: number,
  offsetMs = 0,
): DistributionMs {
  const toMs = (micros: number | null): number | null =>
    micros === null ? null : micros / MICROS_PER_MS + offsetMs;

  return {
    count: totalCount(spec, histogram, baseI32),
    p50: toMs(percentileMicros(spec, histogram, baseI32, 0.5)),
    p90: toMs(percentileMicros(spec, histogram, baseI32, 0.9)),
    p95: toMs(percentileMicros(spec, histogram, baseI32, 0.95)),
    p99: toMs(percentileMicros(spec, histogram, baseI32, 0.99)),
    max: toMs(percentileMicros(spec, histogram, baseI32, 1)),
    overflow: overflowCount(spec, histogram, baseI32),
    bucketMs: spec.BUCKET_MICROS / MICROS_PER_MS,
  };
}

/**
 * Read a distribution **and consume it**, or refuse and leave it untouched (P2.10).
 *
 * The unit a repeated report is built from. A reporter that sent a running total every minute
 * would send the same measurement over and over, weighted more heavily towards the start of the
 * session each time; windows can be aggregated afterwards, overlapping totals cannot be pulled
 * apart. So: take it, and start the next one empty.
 *
 * `minimumCount` is the caller's idea of how many readings make a distribution — a p90 over four
 * samples is the worst of four samples wearing a percentile's name. A window under the floor is
 * **left in place**, not dropped, so a thin stretch rolls forward into the next one instead of
 * being thrown away. That is the whole reason this is one function rather than a read followed by
 * a clear at the call site: the two must not be able to drift apart.
 *
 * Null rather than an empty distribution, for the reason every null in this file exists: "nothing
 * was measured" and "everything measured zero" are different facts, and a function that returns
 * the same value for both is how a budget passes vacuously.
 *
 * A sample landing during the clear can be counted in the next window instead of this one. It
 * cannot be lost — the `Atomics.add` either lands before the store or survives it — and the
 * alternative is a lock shared with a real-time thread, which is not a trade this file will make
 * for a metric.
 */
export function takeWindow(
  spec: HistogramSpec,
  histogram: Int32Array,
  baseI32: number,
  minimumCount = 1,
  offsetMs = 0,
): DistributionMs | null {
  const distribution = readDistribution(spec, histogram, baseI32, offsetMs);

  if (distribution.count < Math.max(1, minimumCount)) {
    return null;
  }

  clearHistogram(spec, histogram, baseI32);
  return distribution;
}
